import * as Dialog from "@radix-ui/react-dialog"
import { router } from "@inertiajs/react"
import { Loader2, X } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import type { ShotMediaVersionPromptVersion } from "@/lib/workspace-api"
import { activateShotPromptVersion } from "@/lib/workspace-api"

type Props = {
  projectId: number
  shotId: number
  promptVersion: ShotMediaVersionPromptVersion
  open: boolean
  onOpenChange: (open: boolean) => void
  onDataChanged?: () => void
}

export function MediaVersionPromptDialog({
  projectId,
  shotId,
  promptVersion,
  open,
  onOpenChange,
  onDataChanged,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const [activating, setActivating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCopy = async () => {
    const text = [promptVersion.system_prompt, promptVersion.user_prompt].filter(Boolean).join("\n\n---\n\n")
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      setError(t("common.copyClipboardFailed"))
    }
  }

  const handleActivate = async () => {
    setActivating(true)
    setError(null)
    try {
      await activateShotPromptVersion(projectId, shotId, promptVersion.id)
      onDataChanged?.()
      router.reload()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setActivating(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[111] flex max-h-[85vh] w-[min(560px,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated">
          <div className="flex items-center justify-between border-b border-ui-border px-4 py-3">
            <Dialog.Title className="text-sm font-semibold text-ui-text-primary">
              {t("mediaVersionPrompt.title")}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="it2tv-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {error ? <p className="text-sm text-ui-status-failed">{error}</p> : null}
            {promptVersion.system_prompt ? (
              <details className="rounded-lg border border-ui-border dark:bg-black/25 light:bg-clay-1">
                <summary className="cursor-pointer px-3 py-2 text-[11px] font-medium tracking-wide text-ui-text-muted uppercase">
                  {t("common.systemPrompt")}
                </summary>
                <pre className="max-h-40 overflow-auto px-3 pb-3 font-mono text-[11px] leading-relaxed break-words whitespace-pre-wrap text-ui-text-secondary">
                  {promptVersion.system_prompt}
                </pre>
              </details>
            ) : null}
            <div>
              <p className="text-[11px] font-medium tracking-wide text-ui-text-muted uppercase">
                {t("mediaVersionPrompt.promptLabel")}
              </p>
              <pre className="mt-1.5 max-h-[50vh] overflow-auto rounded-lg border border-ui-border p-3 font-mono text-[11px] leading-relaxed break-words whitespace-pre-wrap text-ui-text-primary dark:bg-black/25 light:bg-clay-1">
                {promptVersion.user_prompt || "—"}
              </pre>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-ui-border px-4 py-3">
            <Button type="button" size="sm" variant="secondary" onClick={() => void handleCopy()}>
              {t("common.copy")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={activating}
              onClick={() => void handleActivate()}
            >
              {activating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("mediaVersionPrompt.makeActive")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
